/**
 * Iter-225v — Client → Talent Reviews badge.
 *
 * Premium-marketplace aesthetic to match the redesigned <ClientReputationBadge>.
 * Surfaces what clients have said about a given talent so other clients and
 * the talent themselves can see the social proof.
 *
 * Variants:
 *   • compact — small inline summary for talent cards
 *   • detail  — full hero + recent reviews list for the talent profile
 */
import React, { useEffect, useState } from 'react';
import {
  Star, Repeat, MessageSquareQuote, Calendar, MapPin, Briefcase,
  ThumbsUp, ThumbsDown,
} from 'lucide-react';
import api from '../utils/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return ''; }
};

const StarRow = ({ value, size = 'w-4 h-4' }) => {
  if (value == null) return null;
  const rounded = Math.round(value * 2) / 2;
  return (
    <span className="inline-flex items-center" aria-label={`${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= rounded ? 'text-amber-400 fill-amber-400' : 'text-zinc-300 fill-zinc-200'}`}
        />
      ))}
    </span>
  );
};

/** Circular avatar with initials fallback — reused pattern from ClientReputationBadge. */
const Avatar = ({ photoUrl, name, size = 'w-10 h-10' }) => {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name || 'Reviewer'}
        className={`${size} rounded-full object-cover ring-2 ring-white shadow-sm`}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <span
      className={`${size} rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 text-zinc-700 flex items-center justify-center text-xs font-semibold ring-2 ring-white shadow-sm`}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

const RehireChip = ({ rehire }) => {
  if (rehire == null) return null;
  if (rehire) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
        <ThumbsUp className="w-3 h-3 fill-emerald-500 text-emerald-600" />
        Rehire recommended
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded-full">
      <ThumbsDown className="w-3 h-3 text-orange-600" />
      Did not recommend rehire
    </span>
  );
};

export const TalentReviewsBadge = ({ talentId, variant = 'compact', limit = 5 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!talentId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/talents/${talentId}/reviews?recent_limit=${limit}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [talentId, limit]);

  if (loading || !talentId || !data) return null;
  const isEmpty = (data.total_reviews || 0) === 0;

  /* ─── Compact — talent card line ─── */
  if (variant === 'compact') {
    if (isEmpty) {
      return (
        <span className="inline-flex items-center gap-2 text-xs text-zinc-500 italic" data-testid={`talent-reviews-empty-${talentId}`}>
          No client feedback yet
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 flex-wrap text-xs"
            data-testid={`talent-reviews-compact-${talentId}`}>
        <span className="inline-flex items-center gap-1 text-zinc-800">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="font-semibold">{data.avg_rating?.toFixed(1)}</span>
          <span className="text-zinc-500">
            ({data.total_reviews} {data.total_reviews === 1 ? 'review' : 'reviews'})
          </span>
        </span>
        {data.rehire_pct != null && (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
            <Repeat className="w-3.5 h-3.5" />
            {data.rehire_pct}% rehire
          </span>
        )}
      </span>
    );
  }

  /* ─── Detail — full hero + reviews list for the profile ─── */
  return (
    <div
      className="bg-white rounded-2xl border border-zinc-200 overflow-hidden"
      data-testid={`talent-reviews-detail-${talentId}`}
    >
      {/* Hero */}
      <div className="px-5 pt-5 pb-4 bg-gradient-to-b from-zinc-50/80 to-white">
        <div className="flex items-start gap-4">
          <Avatar
            photoUrl={data.talent_photo_url}
            name={data.talent_name}
            size="w-14 h-14"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-zinc-900 truncate" data-testid="talent-reviews-name">
              {data.talent_name}
            </h3>
            {!isEmpty ? (
              <div className="mt-1 flex items-center gap-3 flex-wrap text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <StarRow value={data.avg_rating} size="w-4 h-4" />
                  <span className="font-semibold text-zinc-900">{data.avg_rating?.toFixed(1)}</span>
                  <span className="text-zinc-500">
                    ({data.total_reviews} {data.total_reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </span>
                {data.rehire_pct != null && (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <Repeat className="w-4 h-4" />
                    <span className="font-semibold">{data.rehire_pct}%</span>
                    <span className="text-emerald-700/70">rehire recommended</span>
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-1 text-sm text-zinc-500 italic">No client feedback yet for this talent.</div>
            )}
          </div>
        </div>

        {!isEmpty && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-white border border-zinc-200 p-3 text-center">
              <div className="text-zinc-500 text-[11px] uppercase tracking-wide font-medium">Events worked</div>
              <div className="mt-0.5 text-xl font-bold text-zinc-900">{data.completed_events_count || 0}</div>
            </div>
            <div className="rounded-xl bg-white border border-zinc-200 p-3 text-center">
              <div className="text-zinc-500 text-[11px] uppercase tracking-wide font-medium">Rehire votes</div>
              <div className="mt-0.5 text-xl font-bold text-zinc-900">
                {data.rehire_count}<span className="text-sm font-medium text-zinc-500">/{data.rehire_responses || 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent reviews */}
      {!isEmpty && (data.recent_reviews || []).length > 0 && (
        <div className="px-5 pb-5">
          <h4 className="text-sm font-semibold text-zinc-800 inline-flex items-center gap-1.5 mb-3 mt-1">
            <MessageSquareQuote className="w-4 h-4 text-zinc-500" />
            Client Feedback
          </h4>
          <ul className="space-y-4">
            {(data.recent_reviews || []).map((rv, idx) => (
              <li key={idx} className="flex items-start gap-3" data-testid={`talent-review-${idx}`}>
                <Avatar
                  photoUrl={rv.client_logo_url}
                  name={rv.client_name}
                  size="w-11 h-11"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-zinc-900" data-testid={`talent-review-${idx}-client`}>
                      {rv.client_name}
                    </span>
                    <RehireChip rehire={rv.rehire} />
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                    {rv.role_name && (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {rv.role_name}
                      </span>
                    )}
                    {(rv.event_name || rv.city) && <span>•</span>}
                    {rv.event_name && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {rv.event_name}
                      </span>
                    )}
                    {rv.city && (
                      <>
                        <span>•</span>
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {rv.city}{rv.state ? `, ${rv.state}` : ''}
                        </span>
                      </>
                    )}
                    {rv.event_date && (
                      <>
                        <span>•</span>
                        <span>{formatDate(rv.event_date)}</span>
                      </>
                    )}
                  </div>
                  <div className="mt-1.5">
                    <StarRow value={rv.stars} size="w-3.5 h-3.5" />
                  </div>
                  {rv.comment ? (
                    <p className="mt-1.5 text-sm text-zinc-800 leading-relaxed">“{rv.comment}”</p>
                  ) : (
                    <p className="mt-1.5 text-xs text-zinc-400 italic">No written feedback.</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TalentReviewsBadge;
